import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeftIcon, ShoppingBagIcon, HeartIcon, ZoomInIcon, XIcon, MinusIcon, PlusIcon } from 'lucide-react';
import { lookbookCollections } from '../data/lookbookData';
export function LookbookDetail() {
  const {
    id
  } = useParams();
  const collection = lookbookCollections.find(c => c.id === id);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const [activeProduct, setActiveProduct] = useState(0);
  if (!collection) {
    return <div className="bg-white min-h-screen pt-32 pb-20 px-6 text-center" data-id="element-302">
        <h1 className="text-2xl font-display tracking-widest uppercase" data-id="element-303">
          Editorial Not Found
        </h1>
        <Link to="/lookbook" className="inline-block mt-8 text-[10px] font-display tracking-widest uppercase border-b border-black" data-id="element-304">
          Back to Lookbook
        </Link>
      </div>;
  }
  const product = collection.products[activeProduct];
  return <div className="bg-white min-h-screen" data-id="element-305">
      {/* Breadcrumb */}
      <div className="pt-28 px-6 max-w-[1800px] mx-auto" data-id="element-306">
        <div className="flex items-center space-x-3 text-[9px] font-display tracking-widest uppercase text-black/40 mb-12" data-id="element-307">
          <Link to="/" className="hover:text-black transition-colors" data-id="element-308">
            Home
          </Link>
          <span data-id="element-309">/</span>
          <Link to="/lookbook" className="hover:text-black transition-colors" data-id="element-310">
            Lookbook
          </Link>
          <span data-id="element-311">/</span>
          <span className="text-black" data-id="element-312">{collection.title}</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="px-6 max-w-[1800px] mx-auto mb-20" data-id="element-313">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-314">
          {/* Image Gallery */}
          <div className="lg:col-span-7" data-id="element-315">
            <div className="aspect-[4/5] bg-grey-divider overflow-hidden mb-4 relative group" data-id="element-316">
              <AnimatePresence mode="wait" data-id="element-317">
                <motion.img key={activeImage} src={collection.gallery[activeImage]} alt={collection.title} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.4
              }} className="w-full h-full object-cover" data-id="element-318" />
              </AnimatePresence>

              {/* Hotspots */}
              {activeImage === 0 && collection.products.map((p, idx) => <motion.div key={idx} initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className="absolute z-10 group/hotspot" style={{
              left: `${p.x}%`,
              top: `${p.y}%`
            }} data-id="element-319">
                    <button onClick={e => {
                e.stopPropagation();
                setActiveProduct(idx);
              }} className="relative w-6 h-6 flex items-center justify-center" data-id="element-320">
                      <span className="absolute inset-0 bg-white rounded-full animate-ping opacity-40" data-id="element-321" />
                      <span className={`relative w-3 h-3 rounded-full border border-white shadow-xl transition-all duration-300 ${activeProduct === idx ? 'bg-black scale-125' : 'bg-white/80'}`} data-id="element-322" />
                    </button>

                    <div className="absolute left-8 top-1/2 -translate-y-1/2 bg-white p-4 shadow-2xl opacity-0 invisible group-hover/hotspot:opacity-100 group-hover/hotspot:visible transition-all duration-300 w-48 border border-grey-divider pointer-events-none group-hover/hotspot:pointer-events-auto" data-id="element-323">
                      <p className="text-[9px] font-display font-bold tracking-widest uppercase mb-1" data-id="element-324">
                        {p.name}
                      </p>
                      <p className="text-[10px] font-light mb-3" data-id="element-325">{p.price}</p>
                      <button onClick={() => setActiveProduct(idx)} className="w-full bg-black text-white py-2 text-[8px] font-display uppercase tracking-widest flex items-center justify-center space-x-2" data-id="element-326">
                        <ShoppingBagIcon className="w-3 h-3" data-id="element-327" />
                        <span data-id="element-328">View Details</span>
                      </button>
                    </div>
                  </motion.div>)}

              <button onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20" data-id="element-329">
                <ZoomInIcon className="w-4 h-4" data-id="element-330" />
              </button>
            </div>

            {/* Thumbnail Strip */}
            <div className="grid grid-cols-4 gap-3" data-id="element-331">
              {collection.gallery.map((img, idx) => <button key={idx} onClick={() => setActiveImage(idx)} className={`aspect-square overflow-hidden relative transition-all ${activeImage === idx ? 'ring-2 ring-black' : 'opacity-60 hover:opacity-100'}`} data-id="element-332">
                  <img src={img} alt={`Pose ${idx + 1}`} className="w-full h-full object-cover" data-id="element-333" />
                </button>)}
            </div>
          </div>

          {/* Product Info */}
          <div className="lg:col-span-5 lg:sticky lg:top-32 h-fit" data-id="element-334">
            <div className="border-l border-grey-divider pl-8 lg:pl-12" data-id="element-335">
              <Link to="/lookbook" className="inline-flex items-center space-x-2 text-[10px] font-display tracking-widest uppercase text-black/40 hover:text-black transition-colors mb-8" data-id="element-336">
                <ChevronLeftIcon className="w-3 h-3" data-id="element-337" />
                <span data-id="element-338">Back to Lookbook</span>
              </Link>

              <span className="text-[10px] font-display uppercase tracking-[0.4em] mb-4 block text-black/40" data-id="element-339">
                Featuring {collection.model}
              </span>
              <h1 className="text-3xl md:text-4xl font-display font-bold tracking-[0.2em] uppercase mb-6 leading-tight" data-id="element-340">
                {collection.title}
              </h1>
              <p className="text-sm font-light tracking-wider text-black/70 leading-relaxed mb-12" data-id="element-341">
                {collection.narrative}
              </p>

              {/* Product Variation Selector */}
              {collection.products.length > 1 && <div className="mb-10" data-id="element-342">
                  <h4 className="text-[10px] font-display font-bold tracking-widest uppercase mb-4 text-black/40" data-id="element-343">
                    Featured Pieces
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-344">
                    {collection.products.map((p, idx) => <button key={idx} onClick={() => {
                  setActiveProduct(idx);
                  setSelectedColor(0);
                  setSelectedSize(null);
                }} className={`px-4 py-2 text-[10px] font-display tracking-widest uppercase border transition-colors ${activeProduct === idx ? 'border-black bg-black text-white' : 'border-grey-divider hover:border-black'}`} data-id="element-345">
                        {p.name}
                      </button>)}
                  </div>
                </div>}

              {/* Active Product Details */}
              <div className="space-y-8" data-id="element-346">
                <div className="flex justify-between items-end border-b border-grey-divider pb-6" data-id="element-347">
                  <div data-id="element-348">
                    <p className="text-[10px] font-display font-bold tracking-widest uppercase mb-2 text-black/40" data-id="element-349">
                      Featured Piece
                    </p>
                    <h2 className="text-xl font-display font-bold tracking-widest uppercase" data-id="element-350">
                      {product.name}
                    </h2>
                  </div>
                  <p className="text-xl font-display tracking-widest" data-id="element-351">
                    {product.price}
                  </p>
                </div>

                {/* Color Selector */}
                <div data-id="element-352">
                  <div className="flex justify-between items-baseline mb-4" data-id="element-353">
                    <h4 className="text-[10px] font-display font-bold tracking-widest uppercase" data-id="element-354">
                      Color
                    </h4>
                    <p className="text-[10px] font-display tracking-widest uppercase text-black/60" data-id="element-355">
                      {product.colors[selectedColor].name}
                    </p>
                  </div>
                  <div className="flex space-x-3" data-id="element-356">
                    {product.colors.map((color, idx) => <button key={idx} onClick={() => setSelectedColor(idx)} className={`w-10 h-10 rounded-full border-2 transition-all ${selectedColor === idx ? 'border-black scale-110' : 'border-grey-divider hover:border-black/40'}`} style={{
                    backgroundColor: color.hex
                  }} aria-label={color.name} data-id="element-357" />)}
                  </div>
                </div>

                {/* Size Selector */}
                <div data-id="element-358">
                  <div className="flex justify-between items-baseline mb-4" data-id="element-359">
                    <h4 className="text-[10px] font-display font-bold tracking-widest uppercase" data-id="element-360">
                      Size
                    </h4>
                    <button className="text-[10px] font-display tracking-widest uppercase text-black/60 border-b border-black/20 hover:border-black" data-id="element-361">
                      Size Guide
                    </button>
                  </div>
                  <div className="grid grid-cols-5 gap-2" data-id="element-362">
                    {product.sizes.map(size => <button key={size} onClick={() => setSelectedSize(size)} className={`py-3 text-[11px] font-display font-bold tracking-widest border transition-colors ${selectedSize === size ? 'border-black bg-black text-white' : 'border-grey-divider hover:border-black'}`} data-id="element-363">
                        {size}
                      </button>)}
                  </div>
                </div>

                {/* Quantity */}
                <div data-id="element-364">
                  <h4 className="text-[10px] font-display font-bold tracking-widest uppercase mb-4" data-id="element-365">
                    Quantity
                  </h4>
                  <div className="inline-flex items-center border border-grey-divider" data-id="element-366">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-3 hover:bg-black/5 transition-colors" data-id="element-367">
                      <MinusIcon className="w-3 h-3" data-id="element-368" />
                    </button>
                    <span className="px-6 text-[11px] font-display font-bold tracking-widest" data-id="element-369">
                      {quantity}
                    </span>
                    <button onClick={() => setQuantity(quantity + 1)} className="p-3 hover:bg-black/5 transition-colors" data-id="element-370">
                      <PlusIcon className="w-3 h-3" data-id="element-371" />
                    </button>
                  </div>
                </div>

                {/* CTAs */}
                <div className="space-y-3 pt-4" data-id="element-372">
                  <button className="w-full bg-black text-white py-4 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-black/80 transition-colors flex items-center justify-center space-x-3 disabled:opacity-40 disabled:cursor-not-allowed" disabled={!selectedSize} data-id="element-373">
                    <ShoppingBagIcon className="w-4 h-4" data-id="element-374" />
                    <span data-id="element-375">{selectedSize ? 'Add to Bag' : 'Select a Size'}</span>
                  </button>
                  <button className="w-full border border-black py-4 text-[10px] font-display font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-colors flex items-center justify-center space-x-3" data-id="element-376">
                    <HeartIcon className="w-4 h-4" data-id="element-377" />
                    <span data-id="element-378">Add to Wishlist</span>
                  </button>
                </div>

                {/* Meta */}
                <div className="pt-6 space-y-2 text-[10px] font-display tracking-widest uppercase text-black/50" data-id="element-379">
                  <p data-id="element-380">Free shipping over $300</p>
                  <p data-id="element-381">Bespoke alterations available in-studio</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial Spread */}
      <section className="px-6 max-w-[1800px] mx-auto mb-24" data-id="element-382">
        <div className="text-center mb-16" data-id="element-383">
          <span className="text-[10px] font-display uppercase tracking-[0.5em] mb-4 block text-black/40" data-id="element-384">
            The Editorial
          </span>
          <h2 className="text-2xl md:text-4xl font-display font-bold tracking-[0.2em] uppercase" data-id="element-385">
            {collection.model} in Motion
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-1" data-id="element-386">
          {collection.gallery.map((img, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: idx * 0.1
        }} onClick={() => {
          setActiveImage(idx);
          setIsZoomed(true);
        }} className={`relative overflow-hidden bg-grey-divider cursor-zoom-in ${idx % 3 === 0 ? 'aspect-[4/5]' : 'aspect-[3/4]'}`} data-id="element-387">
              <img src={img} alt={`Editorial pose ${idx + 1}`} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" data-id="element-388" />
              <div className="absolute bottom-6 left-6 text-white text-[9px] font-display tracking-widest uppercase drop-shadow-md" data-id="element-389">
                Look {String(idx + 1).padStart(2, '0')}
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* You May Also Like */}
      <section className="px-6 max-w-[1800px] mx-auto pb-20 border-t border-grey-divider pt-20" data-id="element-390">
        <h2 className="text-2xl font-display font-bold tracking-widest uppercase mb-12 text-center" data-id="element-391">
          You May Also Like
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-392">
          {lookbookCollections.filter(c => c.id !== collection.id).slice(0, 4).map(c => <Link to={`/lookbook/${c.id}`} key={c.id} className="group" data-id="element-393">
                <div className="aspect-[3/4] bg-grey-divider overflow-hidden mb-4" data-id="element-394">
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-395" />
                </div>
                <h3 className="text-[11px] font-display font-bold tracking-widest uppercase mb-1" data-id="element-396">
                  {c.title}
                </h3>
                <p className="text-[10px] font-display tracking-widest uppercase text-black/40" data-id="element-397">
                  {c.model}
                </p>
              </Link>)}
        </div>
      </section>

      {/* Zoom Modal */}
      <AnimatePresence data-id="element-398">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-8" onClick={() => setIsZoomed(false)} data-id="element-399">
            <button onClick={() => setIsZoomed(false)} className="absolute top-6 right-6 p-3 text-white hover:bg-white/10 rounded-full transition-colors" data-id="element-400">
              <XIcon className="w-6 h-6" data-id="element-401" />
            </button>
            <motion.img initial={{
          scale: 0.9
        }} animate={{
          scale: 1
        }} src={collection.gallery[activeImage]} alt={collection.title} className="max-h-full max-w-full object-contain" onClick={e => e.stopPropagation()} data-id="element-402" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}